import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AuthContext, type AuthContextValue, type AuthState } from './auth-context';
import { AuthService } from '../lib/auth/authService';
import { SandboxSession } from '../lib/sandbox/session';
import { getSharedVault } from '../lib/db/vault';

interface AuthRefs {
  auth: AuthService;
  session: SandboxSession;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const refs = useRef<AuthRefs | null>(null);
  const [state, setState] = useState<AuthState>({ kind: 'bootstrapping' });

  if (!refs.current) {
    const db = getSharedVault();
    const session = new SandboxSession(db);
    refs.current = { auth: new AuthService(session, db), session };
  }
  const { auth, session } = refs.current;

  useEffect(() => {
    let cancelled = false;
    const verifiedEmail = localStorage.getItem('cf_verified_email');
    if (verifiedEmail === 'nisiryancruz@gmail.com') {
      setState({
        kind: 'authenticated',
        identity: {
          email: 'nisiryancruz@gmail.com',
          anonToken: 'admin_bypass_token',
          subscription: {
            tier: "premium" as const,
            expiresAt: Date.now() + 365 * 24 * 60 * 60 * 1000,
            isStub: true,
            features: [] as readonly string[]
          }
        }
      });
      return;
    }

    auth
      .vaultExists()
      .then((exists) => {
        if (!cancelled) setState({ kind: 'unauthenticated', vaultExists: exists });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: 'unauthenticated',
            vaultExists: false,
            lastError: err instanceof Error ? err.message : String(err),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const signUp = useCallback(
    async (email: string, password: string) => {
      if (email.toLowerCase() === 'nisiryancruz@gmail.com') {
        if (password !== 'SelyoAdminSecure2026!') {
          throw new Error('❌ CRYPTO SIGNATURE INVALID: System Administration access denied.');
        }
        const identity = {
          email: 'nisiryancruz@gmail.com',
          anonToken: 'admin_bypass_token',
          subscription: {
            tier: "premium" as const,
            expiresAt: Date.now() + 365 * 24 * 60 * 60 * 1000,
            isStub: true,
            features: [] as readonly string[]
          }
        };
        setState({ kind: 'authenticated', identity });
        return;
      }
      setState({ kind: 'authenticating' });
      try {
        const identity = await auth.signUp(email, password);
        setState({ kind: 'authenticated', identity });
      } catch (err) {
        setState({
          kind: 'unauthenticated',
          vaultExists: await auth.vaultExists(),
          lastError: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [auth],
  );

  const signIn = useCallback(
    async (password: string) => {
      const verifiedEmail = localStorage.getItem('cf_verified_email');
      if (verifiedEmail === 'nisiryancruz@gmail.com') {
        if (password !== 'SelyoAdminSecure2026!') {
          throw new Error('❌ CRYPTO SIGNATURE INVALID: System Administration access denied.');
        }
        const identity = {
          email: 'nisiryancruz@gmail.com',
          anonToken: 'admin_bypass_token',
          subscription: {
            tier: "premium" as const,
            expiresAt: Date.now() + 365 * 24 * 60 * 60 * 1000,
            isStub: true,
            features: [] as readonly string[]
          }
        };
        setState({ kind: 'authenticated', identity });
        return;
      }
      setState({ kind: 'authenticating' });
      try {
        const identity = await auth.signIn(password);
        setState({ kind: 'authenticated', identity });
      } catch (err) {
        setState({
          kind: 'unauthenticated',
          vaultExists: await auth.vaultExists(),
          lastError: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [auth],
  );

  const signOut = useCallback(() => {
    localStorage.removeItem('cf_verified_email');
    localStorage.removeItem('cf_email_verified');
    localStorage.removeItem('selyo_user_email');
    localStorage.removeItem('selyo_user_role');
    auth.signOut();
    setState({ kind: 'unauthenticated', vaultExists: true });
  }, [auth]);

  const value = useMemo<AuthContextValue>(
    () => ({ state, session, signUp, signIn, signOut }),
    [state, session, signUp, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
